import qrcode from 'qrcode-generator';

// Encode text as UTF-8 (WhatsApp messages can contain any characters).
qrcode.stringToBytes = qrcode.stringToBytesFuncs['UTF-8'];

export type QrType =
  | 'website'
  | 'googlePlay'
  | 'appStore'
  | 'instagram'
  | 'facebook'
  | 'tiktok'
  | 'whatsapp'
  | 'phone';

export interface QrInput {
  value: string;
  message?: string;
}

export const QR_TYPES: {
  type: QrType;
  label: string;
  field: string;
  placeholder: string;
}[] = [
  {
    type: 'website',
    label: 'Website',
    field: 'Website address',
    placeholder: 'example.com/menu',
  },
  {
    type: 'googlePlay',
    label: 'Google Play',
    field: 'App package name or Google Play link',
    placeholder: 'com.example.app',
  },
  {
    type: 'appStore',
    label: 'App Store',
    field: 'App ID or App Store link',
    placeholder: 'id123456789',
  },
  {
    type: 'instagram',
    label: 'Instagram',
    field: 'Instagram username or profile link',
    placeholder: '@yourbrand',
  },
  {
    type: 'facebook',
    label: 'Facebook',
    field: 'Facebook page name or link',
    placeholder: 'yourbrand',
  },
  {
    type: 'tiktok',
    label: 'TikTok',
    field: 'TikTok username or profile link',
    placeholder: '@yourbrand',
  },
  {
    type: 'whatsapp',
    label: 'WhatsApp',
    field: 'WhatsApp number with country code',
    placeholder: '+1 555 123 4567',
  },
  {
    type: 'phone',
    label: 'Phone call',
    field: 'Phone number with country code',
    placeholder: '+1 555 123 4567',
  },
];

const parseUrl = (value: string) => {
  try {
    return new URL(/^[a-z]+:\/\//i.test(value) ? value : `https://${value}`);
  } catch {
    return null;
  }
};

const isOnHost = (url: URL | null, host: string) =>
  !!url && (url.hostname === host || url.hostname.endsWith(`.${host}`));

const firstPathSegment = (url: URL) =>
  url.pathname.split('/').filter(Boolean)[0] || '';

const phoneDigits = (value: string) => {
  const digits = value.replace(/[\s().-]/g, '').replace(/^(\+|00)/, '');
  return /^\d{6,15}$/.test(digits) ? digits : null;
};

const username = (value: string, host: string, pattern: RegExp) => {
  const trimmed = value.trim();
  // Usernames can contain dots; only a slash makes it a link.
  const url = trimmed.includes('/') ? parseUrl(trimmed) : null;
  const name = (
    isOnHost(url, host) && url ? firstPathSegment(url) : trimmed
  ).replace(/^@/, '');
  return pattern.test(name) ? name : null;
};

// The text the QR code encodes (a link), or null when the input is not
// valid for the type.
export const buildQrContent = (type: QrType, input: QrInput): string | null => {
  const value = input.value.trim();
  if (!value) return null;
  switch (type) {
    case 'website': {
      const url = parseUrl(value);
      return url && url.hostname.includes('.') ? url.href : null;
    }
    case 'googlePlay': {
      const url = parseUrl(value);
      const id = isOnHost(url, 'play.google.com')
        ? url?.searchParams.get('id') || ''
        : value;
      return /^[a-zA-Z][\w]*(\.[a-zA-Z_][\w]*)+$/.test(id)
        ? `https://play.google.com/store/apps/details?id=${id}`
        : null;
    }
    case 'appStore': {
      const url = parseUrl(value);
      if (isOnHost(url, 'apps.apple.com') && url) {
        const id = url.pathname.match(/id(\d+)/)?.[1];
        return id ? `https://apps.apple.com/app/id${id}` : null;
      }
      const id = value.replace(/^id/i, '');
      return /^\d{5,}$/.test(id) ? `https://apps.apple.com/app/id${id}` : null;
    }
    case 'instagram': {
      const name = username(value, 'instagram.com', /^[A-Za-z0-9._]{1,30}$/);
      return name ? `https://www.instagram.com/${name}/` : null;
    }
    case 'facebook': {
      const url = parseUrl(value);
      if (isOnHost(url, 'facebook.com') && url && url.pathname !== '/') {
        return `https://www.facebook.com${url.pathname}${url.search}`;
      }
      return /^[A-Za-z0-9.-]{2,}$/.test(value)
        ? `https://www.facebook.com/${value}`
        : null;
    }
    case 'tiktok': {
      const name = username(value, 'tiktok.com', /^[A-Za-z0-9._]{2,24}$/);
      return name ? `https://www.tiktok.com/@${name}` : null;
    }
    case 'whatsapp': {
      const digits = phoneDigits(value);
      if (!digits) return null;
      const message = input.message?.trim();
      return message
        ? `https://wa.me/${digits}?text=${encodeURIComponent(message)}`
        : `https://wa.me/${digits}`;
    }
    case 'phone': {
      const digits = phoneDigits(value);
      return digits ? `tel:+${digits}` : null;
    }
  }
};

// Light margin around the code, in modules (scanners need one).
const QUIET_ZONE = 2;
const round = (value: number) => Number(value.toFixed(3));

// The QR code as an SVG path on a 100x100 grid, one rectangle per run of
// dark modules. It starts with moves to both corners so the path's bounds
// are the full 100x100 box players draw it in.
export const buildQrPath = (content: string) => {
  const qr = qrcode(0, 'M');
  qr.addData(content, 'Byte');
  qr.make();
  const count = qr.getModuleCount();
  const unit = 100 / (count + QUIET_ZONE * 2);
  const parts = ['M0 0M100 100'];
  for (let row = 0; row < count; row++) {
    let col = 0;
    while (col < count) {
      if (!qr.isDark(row, col)) {
        col++;
        continue;
      }
      const start = col;
      while (col < count && qr.isDark(row, col)) col++;
      const x = round((start + QUIET_ZONE) * unit);
      const y = round((row + QUIET_ZONE) * unit);
      const width = round((col - start) * unit);
      parts.push(`M${x} ${y}h${width}v${round(unit)}h${-width}z`);
    }
  }
  return parts.join('');
};
